import { useState } from 'react';
import { Sparkles, X, Zap, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { resetDemoContent } from '@/utils/resetDemoContent';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useNativeConfirmation } from '@/hooks/useNativeConfirmation';
import { supabase } from '@/integrations/supabase/client';
interface DemoBannerProps {
  familyId: string;
  onReset?: () => void;
}
export function DemoBanner({
  familyId,
  onReset
}: DemoBannerProps) {
  const [isResetting, setIsResetting] = useState(false);
  const [isGoingLive, setIsGoingLive] = useState(false);
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    showConfirmation
  } = useNativeConfirmation();
  const handleGoLive = async () => {
    const confirmed = await showConfirmation({
      title: 'Passer en mode réel ?',
      message: 'Votre contenu de démonstration sera conservé, mais ne sera plus marqué comme tel. Vous pourrez continuer à l\'utiliser normalement.',
      okButtonTitle: 'Passer en mode réel',
      cancelButtonTitle: 'Annuler'
    });
    if (!confirmed) return;
    setIsGoingLive(true);
    try {
      const {
        error
      } = await supabase.from('families').update({
        demo_content_active: false
      }).eq('id', familyId);
      if (error) throw error;

      // Invalidate all queries to refresh the UI
      await queryClient.invalidateQueries({
        queryKey: ['demo-mode']
      });
      toast({
        title: 'Mode réel activé',
        description: 'Votre contenu est maintenant en mode réel'
      });
      onReset?.();
    } catch (error) {
      console.error('Error going live:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de passer en mode réel',
        variant: 'destructive'
      });
    } finally {
      setIsGoingLive(false);
    }
  };
  const handleReset = async () => {
    const confirmed = await showConfirmation({
      title: 'Réinitialiser le contenu de démonstration ?',
      message: 'Cela supprimera toutes les tâches, événements, contacts et notes de démonstration. Vos membres de famille seront conservés.',
      okButtonTitle: 'Réinitialiser',
      cancelButtonTitle: 'Annuler'
    });
    if (!confirmed) return;
    setIsResetting(true);
    try {
      await resetDemoContent(familyId);

      // Invalidate all queries to refresh the UI
      await queryClient.invalidateQueries();
      toast({
        title: 'Contenu réinitialisé',
        description: 'Le contenu de démonstration a été supprimé avec succès'
      });
      onReset?.();
    } catch (error) {
      console.error('Error resetting demo content:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de réinitialiser le contenu',
        variant: 'destructive'
      });
    } finally {
      setIsResetting(false);
    }
  };
  return (
    <div className="fixed top-3 left-4 right-4 z-50 flex justify-center pointer-events-none">
      <div className="pointer-events-auto bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/60 rounded-2xl px-4 py-2.5 shadow-lg shadow-black/5 flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-500 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Mode démo
          </span>
        </div>
        
        <Button
          size="sm"
          onClick={handleGoLive}
          disabled={isGoingLive}
          className="h-7 px-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-xs font-medium shadow-sm"
        >
          <Zap className="h-3 w-3 mr-1" />
          Go live
        </Button>
      </div>
    </div>
  );
}